import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ZONE_COLLECTION } from 'constants/collectionConstants';
import type { IFarm } from 'interfaces/IFarm';
import PaddockService from 'services/PaddockService';

import {
  readCachedFarm,
  readZoneIdsByPaddockIds,
  removeZonesFromCache,
  writeFarmToCache,
} from './farmsCache';

interface DeletePaddockArgs {
  farmId: string;
  paddockId: string;
}

/**
 * Deletes a paddock and drops it from its farm in the cache, so it leaves the
 * Paddocks lens and the tree straight away (OP-2481). All cascade-deleted
 * zones (default and custom) are identified from the response `changes` array
 * and removed from the zones cache.
 *
 * Fallback: if `changes` carries no zone records, the zones cache is
 * reverse-looked up via zone.links to find all zones (default and custom)
 * linked to the deleted paddock — consistent with the upsert cache strategy.
 */
export const useDeletePaddock = (orgId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ farmId, paddockId }: DeletePaddockArgs) =>
      PaddockService.Delete(farmId, paddockId),
    onSuccess: (response, { farmId, paddockId }) => {
      if (!orgId) return;

      // data[0] is always the updated Farm for delete operations. savedFarm
      // still contains the soft-deleted paddock (isActive: false), which is
      // used below for the zone fallback before being filtered out.
      const savedFarm = response?.data?.[0] as IFarm | undefined;

      // Read from cache before writing — needed for the zone fallback when
      // savedFarm is absent.
      const cachedFarm = savedFarm
        ? undefined
        : readCachedFarm(queryClient, orgId, farmId);

      if (savedFarm) {
        writeFarmToCache(queryClient, orgId, {
          ...savedFarm,
          paddocks: savedFarm.paddocks.filter((p) => p.isActive),
        });
      } else if (cachedFarm) {
        writeFarmToCache(queryClient, orgId, {
          ...cachedFarm,
          paddocks: cachedFarm.paddocks.filter(
            (p) => p.paddockId !== paddockId
          ),
        });
      }

      const deletedZoneIds = (response?.changes ?? [])
        .filter((c) => c.collection === ZONE_COLLECTION)
        .map((c) => c.docId);

      if (deletedZoneIds.length > 0) {
        removeZonesFromCache(queryClient, orgId, deletedZoneIds);
      } else {
        // Fallback: reverse-lookup all zones (default and custom) linked to
        // this paddock via zone.links. paddock.zoneId only covers the default
        // zone, so the cache is the authoritative source here.
        const fallbackZoneIds = readZoneIdsByPaddockIds(queryClient, orgId, [
          paddockId,
        ]);
        if (fallbackZoneIds.length > 0) {
          removeZonesFromCache(queryClient, orgId, fallbackZoneIds);
        }
      }
    },
  });
};

export default useDeletePaddock;
